import Input from "../../components/form/input/InputField"
import Label from "../../components/form/Label";
import Select from "../../components/form/Select";

import { Controller } from "react-hook-form";
import type { UseFormRegister, FieldValues, Control, Path } from "react-hook-form";
import type { MenuItem } from "../../types/menuItemTypes";
import { findCountryByCurrencyCode } from "../../lib/countries/countries.utils";

const UNITS = [
  { value: "g", label: "Gram" },
  { value: "kg", label: "Kilogram" },
  { value: "oz", label: "Ounce", },
  { value: "ml", label: "Milliliter" },
  { value: "L", label: "Liter" },
  { value: "fl_oz", label: "Fluid Ounce", },
];

type PriceAmountInput<T extends FieldValues> = {
  menuItem?: MenuItem;
  register: UseFormRegister<T>;
  control: Control<T>
  currencyCode?: string
}

const PriceAmountInput = <T extends FieldValues>({
  register,
  control,
  currencyCode
}: PriceAmountInput<T>) => {
  const currency = findCountryByCurrencyCode(currencyCode)

  return (
    <div className="overflow-y-auto custom-scrollbar">
      <div className="grid grid-cols-1 gap-x-6 gap-y-5 lg:grid-cols-2">
        <div className="rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
          <Label>Amount</Label>
          <div className='flex items-center'>
            <Input
              id="amount"
              parentClassName='w-full'
              placeholder='Enter amount value'
              {...register('amount' as Path<T>, { valueAsNumber: true })}
              type="number"
            />
          </div>
        </div>
        <div className="rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
          <Label>Price *</Label>          
          <Controller
            name={"price" as Path<T>}
            control={control}
            rules={{ required: 'Price is required' }}
            render={({ field, fieldState: { error }}) => (
              <>
                <div className={`flex items-center ${currency?.currency.prefix ? 'flex-row-reverse' : ''}`}>
                  <Input
                    parentClassName='w-full'
                    type="number"
                    placeholder='Enter price value'
                    step={0.01}
                    min="0.01"
                    error={Boolean(error)}
                    {...field}
                  />
                  <span className="mx-2 text-sm font-medium text-gray-800 dark:text-gray-200">
                    {currency?.currency.symbol}
                  </span>
                </div>
                {error && <span className='text-sm text-error-500'>{error.message}</span>}
              </>
            )}
          />    
        </div>
        <div className="rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
          <Label>Unit</Label>
          <div className='flex items-center'>
            <Controller
              name={"unit" as Path<T>}
              control={control}
              render={({ field }) => (
                <Select
                  options={UNITS}
                  placeholder='Select unit'
                  defaultValue={field.value}
                  className="dark:bg-dark-900"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    // Note: setValue is not defined; import UseFormSetValue and add setValue prop if needed
                  }}
                />
              )}
            />
          </div>
        </div>
        <div className="rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
          <Label>Mass</Label>
          <div className={`flex items-center`}>
            <Input
              id="mass"
              parentClassName='w-full'
              type="number"
              placeholder='Enter mass'
              {...register('mass' as Path<T>, { valueAsNumber: true })}
            />
          </div>
        </div>
      </div>  
    </div>
  )
}

export default PriceAmountInput
